import { lazy } from "react";
import { Navigate } from "react-router-dom";

// project-imports
import Loadable from "components/Loadable";

// Code-split the admin layout itself, same reasoning as MainRoutes.jsx's
// DashboardLayout — was eagerly imported, dragging its full nav/menu tree
// into every route's chunk including the public login page.
const DashboardLayout = Loadable(lazy(() => import("layout/Admin")));

const MaintenanceError = Loadable(
  lazy(() => import("pages/admin/maintenance/error/404")),
);

/* -------------------------------------------------------------------------- */
/*                                  DASHBOARD                                 */
/* -------------------------------------------------------------------------- */
const Dashboard = Loadable(lazy(() => import("pages/admin/dashboard/page")));

/* -------------------------------------------------------------------------- */
/*                                MÉDIATHÈQUE                                 */
/* -------------------------------------------------------------------------- */
const Mediatheque = Loadable(lazy(() => import("pages/admin/mediatheque/list")));

/* -------------------------------------------------------------------------- */
/*                                   USERS                                    */
/* -------------------------------------------------------------------------- */
const ListUsers = Loadable(lazy(() => import("pages/admin/users/list")));
const UserProfile = Loadable(lazy(() => import("pages/admin/users/profile")));
const RoleGenerate = Loadable(lazy(() => import("pages/admin/roles/generate")));
const SubAdminLogs = Loadable(lazy(() => import("pages/admin/roles/logs")));

/* -------------------------------------------------------------------------- */
/*                                   NOTES                                    */
/* -------------------------------------------------------------------------- */
const AddNote = Loadable(lazy(() => import("pages/admin/notes/add")));
const ListNotes = Loadable(lazy(() => import("pages/admin/notes/list")));
const EditNote = Loadable(lazy(() => import("pages/admin/notes/edit")));
const ImportReviewNotes = Loadable(lazy(() => import("pages/admin/notes/importReview")));
const ImportReviewAvis = Loadable(lazy(() => import("pages/admin/avis/importReview")));

/* -------------------------------------------------------------------------- */
/*                                    Avis                                    */
/* -------------------------------------------------------------------------- */
const AddAvis = Loadable(lazy(() => import("pages/admin/avis/add")));
const ListAvis = Loadable(lazy(() => import("pages/admin/avis/list")));
const EditAvis = Loadable(lazy(() => import("pages/admin/avis/edit")));

/* -------------------------------------------------------------------------- */
/*                                 ACTUALITÉS                                 */
/* -------------------------------------------------------------------------- */
const AddActualite = Loadable(lazy(() => import("pages/admin/actualites/add")));
const ListActualites = Loadable(
  lazy(() => import("pages/admin/actualites/list")),
);
const EditActualite = Loadable(
  lazy(() => import("pages/admin/actualites/edit")),
);

/* -------------------------------------------------------------------------- */
/*                                   EVENTS                                   */
/* -------------------------------------------------------------------------- */
const AddEvent = Loadable(lazy(() => import("pages/admin/events/add")));
const ListEvents = Loadable(lazy(() => import("pages/admin/events/list")));
const EditEvent = Loadable(lazy(() => import("pages/admin/events/edit")));
const ImportZipReviewEvents = Loadable(
  lazy(() => import("pages/admin/events/importZipReview"))
);

/* -------------------------------------------------------------------------- */
/*                                   EXPERTS                                  */
/* -------------------------------------------------------------------------- */
const AddExpert = Loadable(lazy(() => import("pages/admin/experts/add")));
const ListExperts = Loadable(lazy(() => import("pages/admin/experts/list")));
const EditExpert = Loadable(lazy(() => import("pages/admin/experts/edit")));

/* -------------------------------------------------------------------------- */
/*                                     INP                                    */
/* -------------------------------------------------------------------------- */
const ListINP = Loadable(lazy(() => import("pages/admin/inp/list")));
const INPProfile = Loadable(lazy(() => import("pages/admin/inp/profile")));
const ListINPVerifications = Loadable(
  lazy(() => import("pages/admin/verifications/inpList")),
);

/* -------------------------------------------------------------------------- */
/*                                INSTITUTIONS                                */
/* -------------------------------------------------------------------------- */
const AddInstitu = Loadable(lazy(() => import("pages/admin/institutions/add")));
const ListInstitus = Loadable(
  lazy(() => import("pages/admin/institutions/list")),
);
const EditInstitu = Loadable(
  lazy(() => import("pages/admin/institutions/edit")),
);

/* -------------------------------------------------------------------------- */
/*                                 MEDICAMENTS                                */
/* -------------------------------------------------------------------------- */
const AddMedicament = Loadable(
  lazy(() => import("pages/admin/medicaments/add")),
);
const ListMedicaments = Loadable(
  lazy(() => import("pages/admin/medicaments/list")),
);
const EditMedicament = Loadable(
  lazy(() => import("pages/admin/medicaments/edit")),
);

/* -------------------------------------------------------------------------- */
/*                                  Diseases                                  */
/* -------------------------------------------------------------------------- */
const AddDisease = Loadable(lazy(() => import("pages/admin/diseases/add")));
const ListDiseases = Loadable(lazy(() => import("pages/admin/diseases/list")));
const EditDisease = Loadable(lazy(() => import("pages/admin/diseases/edit")));

/* -------------------------------------------------------------------------- */
/*                                NOTIFICATION                                */
/* -------------------------------------------------------------------------- */
const Notifications = Loadable(lazy(() => import("pages/admin/notifications")));
const ContributionDetail = Loadable(
  lazy(() => import("pages/admin/contributions/[id]")),
);
const ContributionsOverview = Loadable(
  lazy(() => import("pages/admin/contributions/index")),
);
const ContributionModerationHub = Loadable(
  lazy(() => import("pages/admin/contributions/ContributionModerationHub")),
);
const ContributionModerationDetail = Loadable(
  lazy(() => import("pages/admin/contributions/ContributionModerationDetail")),
);
const ReportDetail = Loadable(lazy(() => import("pages/admin/reports/[id]")));
const ProfessionChangeRequestDetail = Loadable(
  lazy(() => import("pages/admin/requests/profession-change/[id]")),
);

/* -------------------------------------------------------------------------- */
/*                                    TAGS                                    */
/* -------------------------------------------------------------------------- */
const Tags = Loadable(lazy(() => import("pages/admin/tags")));

/* -------------------------------------------------------------------------- */
/*                                   SOURCES                                  */
/* -------------------------------------------------------------------------- */
const ListSources = Loadable(lazy(() => import("pages/admin/sources/list")));
const AddSource = Loadable(lazy(() => import("pages/admin/sources/add")));
const EditSource = Loadable(lazy(() => import("pages/admin/sources/edit")));
const CategoriesList = Loadable(
  lazy(() => import("pages/admin/tags/categories")),
);
const ProfessionsList = Loadable(
  lazy(() => import("pages/admin/tags/professions")),
);
const SpecialtiesList = Loadable(
  lazy(() => import("pages/admin/tags/specialties")),
);
const DiseaseCategoriesList = Loadable(
  lazy(() => import("pages/admin/tags/disease-categories")),
);
const KeywordsList = Loadable(lazy(() => import("pages/admin/tags/keywords")));
const DCIList = Loadable(lazy(() => import("pages/admin/tags/dci")));
const TherapeuticsList = Loadable(
  lazy(() => import("pages/admin/tags/therapeutics")),
);
const OrganizationTypesList = Loadable(
  lazy(() => import("pages/admin/tags/organization-types")),
);
const FormeGaleniquesList = Loadable(
  lazy(() => import("pages/admin/tags/forme-galeniques")),
);
const FormationCategoriesList = Loadable(
  lazy(() => import("pages/admin/tags/formation-categories")),
);
const SymptomsList = Loadable(lazy(() => import("pages/admin/tags/symptoms")));
const CausesList = Loadable(lazy(() => import("pages/admin/tags/causes")));
const RiskFactorsList = Loadable(
  lazy(() => import("pages/admin/tags/risk-factors")),
);
const GenericGroupsList = Loadable(
  lazy(() => import("pages/admin/tags/generic-groups")),
);
const AdministrationRoutesList = Loadable(
  lazy(() => import("pages/admin/tags/administration-routes")),
);
const EventTypesList = Loadable(
  lazy(() => import("pages/admin/tags/event-types")),
);
const BlogCategoriesList = Loadable(
  lazy(() => import("pages/admin/tags/blog-categories")),
);
const SourceLevels = Loadable(lazy(() => import("pages/admin/source-levels")));

/* -------------------------------------------------------------------------- */
/*                              FORMATION MANAGEMENT                          */
/* -------------------------------------------------------------------------- */
const AddFormation = Loadable(lazy(() => import("pages/admin/formations/add")));
const ListFormations = Loadable(
  lazy(() => import("pages/admin/formations/list")),
);
const EditFormation = Loadable(
  lazy(() => import("pages/admin/formations/edit")),
);
const ImportZipReviewFormations = Loadable(
  lazy(() => import("pages/admin/formations/importZipReview")),
);

/* -------------------------------------------------------------------------- */
/*                               MODULE MANAGEMENT                           */
/* -------------------------------------------------------------------------- */
const ListModules = Loadable(lazy(() => import("pages/admin/modules/list")));
const EditModule = Loadable(lazy(() => import("pages/admin/modules/edit")));

/* -------------------------------------------------------------------------- */
/*                              CERTIFICATIONS                               */
/* -------------------------------------------------------------------------- */
const ListCertificats = Loadable(
  lazy(() => import("pages/admin/certifications/list")),
);
const AddCertificate = Loadable(
  lazy(() => import("pages/admin/certifications/add")),
);
const EditCertificate = Loadable(
  lazy(() => import("pages/admin/certifications/edit")),
);

/* -------------------------------------------------------------------------- */
/*                               GAMIFICATION                                 */
/* -------------------------------------------------------------------------- */
const Gamification = Loadable(lazy(() => import("pages/admin/gamification")));
const VeilleCategory = Loadable(
  lazy(() => import("pages/admin/gamification/category/veille")),
);
const ApprentissageCategory = Loadable(
  lazy(() => import("pages/admin/gamification/category/apprentissage")),
);
const CommunauteCategory = Loadable(
  lazy(() => import("pages/admin/gamification/category/communaute")),
);
const InformationsCategory = Loadable(
  lazy(() => import("pages/admin/gamification/category/informations")),
);
const BadgesManagement = Loadable(
  lazy(() => import("pages/admin/gamification/badges")),
);
const RewardsManagement = Loadable(
  lazy(() => import("pages/admin/gamification/rewards")),
);

/* -------------------------------------------------------------------------- */
/*                                 CORPORATE (removed on smsd)                */
/* -------------------------------------------------------------------------- */
const HeroManagement = Loadable(
  lazy(() => import("pages/admin/heromanagement")),
);
const Waitlist = Loadable(lazy(() => import("pages/admin/waitlist")));

/* -------------------------------------------------------------------------- */
/*                                 INSIGHTS                                   */
/* -------------------------------------------------------------------------- */
const Insights = Loadable(lazy(() => import("pages/admin/insights")));
const AddInsight = Loadable(lazy(() => import("pages/admin/insights/add")));
const ListInsights = Loadable(lazy(() => import("pages/admin/insights/list")));
const EditInsight = Loadable(lazy(() => import("pages/admin/insights/edit")));
const InsightCategories = Loadable(
  lazy(() => import("pages/admin/insights/categories")),
);
const HeroInsightsManagement = Loadable(
  lazy(() => import("pages/admin/hero-insights-management")),
);

/* -------------------------------------------------------------------------- */
/*                                 NEWSLETTERS                                */
/* -------------------------------------------------------------------------- */
const NewsletterDashboard = Loadable(
  lazy(() => import("pages/admin/newsletters/NewsletterDashboard")),
);
const NewsletterCreate = Loadable(
  lazy(() => import("pages/admin/newsletters/NewsletterCreate")),
);
const NewsletterAnalytics = Loadable(
  lazy(() => import("pages/admin/newsletters/NewsletterAnalytics")),
);
const NewsletterSubscribers = Loadable(
  lazy(() => import("pages/admin/newsletter/NewsletterAdmin")),
);

/* -------------------------------------------------------------------------- */
/*                                  LIBRARY                                  */
/* -------------------------------------------------------------------------- */
const AddLibrary = Loadable(lazy(() => import("pages/admin/library/add")));
const ListLibrary = Loadable(lazy(() => import("pages/admin/library/list")));
const EditLibrary = Loadable(lazy(() => import("pages/admin/library/edit")));
const LibraryCategoriesList = Loadable(
  lazy(() => import("pages/admin/tags/library-categories")),
);

const ListBanners = Loadable(lazy(() => import("pages/admin/banners/list")));

const AddBanner = Loadable(lazy(() => import("pages/admin/banners/add")));

const EditBanner = Loadable(lazy(() => import("pages/admin/banners/edit")));

// ==============================|| MAIN ROUTES ||============================== //

const AdminRoutes = {
  path: "/",
  element: <DashboardLayout />,
  children: [
    {
      path: "admin",
      children: [
        {
          index: true,
          element: <Navigate to="dashboard" replace />,
        },
        {
          path: "dashboard",
          element: <Dashboard />,
        },
        {
          path: "mediatheque",
          element: <Mediatheque />,
        },
        {
          path: "add-note",
          element: <AddNote />,
        },
        {
          path: "list-notes",
          element: <ListNotes />,
        },
        {
          path: "notes/import-review",
          element: <ImportReviewNotes />,
        },
        {
          path: "list-users",
          element: <ListUsers />,
        },
        {
          path: "role-generate",
          element: <RoleGenerate />,
        },
        {
          path: "sub-admin-logs",
          element: <SubAdminLogs />,
        },
        {
          path: "users/profile/:id",
          element: <UserProfile />,
        },
        {
          path: "edit-note/:id",
          element: <EditNote />,
        },
        {
          path: "add-avis",
          element: <AddAvis />,
        },
        {
          path: "list-avis",
          element: <ListAvis />,
        },
        {
          path: "edit-avis/:id",
          element: <EditAvis />,
        },
        {
          path: "avis/import-review",
          element: <ImportReviewAvis />,
        },
        {
          path: "list-blogs",
          element: <ListActualites />,
        },
        {
          path: "add-blog",
          element: <AddActualite />,
        },
        {
          path: "edit-blog/:id",
          element: <EditActualite />,
        },
        {
          path: "add-insight",
          element: <AddInsight />,
        },
        {
          path: "list-insights",
          element: <ListInsights />,
        },
        {
          path: "edit-insight/:id",
          element: <EditInsight />,
        },
        {
          path: "insight-categories",
          element: <InsightCategories />,
        },
        {
          path: "add-event",
          element: <AddEvent />,
        },
        {
          path: "list-events",
          element: <ListEvents />,
        },
        {
          path: "edit-event/:id",
          element: <EditEvent />,
        },
        {
          path: "events/import-zip-review",
          element: <ImportZipReviewEvents />,
        },
        {
          path: "add-expert",
          element: <AddExpert />,
        },
        {
          path: "list-experts",
          element: <ListExperts />,
        },
        {
          path: "edit-expert/:id",
          element: <EditExpert />,
        },
        {
          path: "list-inp",
          element: <ListINP />,
        },
        {
          path: "inp/profile/:id",
          element: <INPProfile />,
        },
        {
          path: "inp/verifications",
          element: <ListINPVerifications />,
        },
        {
          path: "add-institution",
          element: <AddInstitu />,
        },
        {
          path: "list-institutions",
          element: <ListInstitus />,
        },
        {
          path: "edit-institution/:id",
          element: <EditInstitu />,
        },
        {
          path: "add-medicament",
          element: <AddMedicament />,
        },
        {
          path: "list-medicaments",
          element: <ListMedicaments />,
        },
        {
          path: "edit-medicament/:id",
          element: <EditMedicament />,
        },
        {
          path: "add-disease",
          element: <AddDisease />,
        },
        {
          path: "list-diseases",
          element: <ListDiseases />,
        },
        {
          path: "edit-disease/:id",
          element: <EditDisease />,
        },
        {
          path: "tags",
          element: <Tags />,
        },
        {
          path: "list-sources",
          element: <ListSources />,
        },
        {
          path: "add-source",
          element: <AddSource />,
        },
        {
          path: "edit-source/:id",
          element: <EditSource />,
        },
        // Tag Management Routes
        {
          path: "tags/categories",
          element: <CategoriesList />,
        },
        {
          path: "tags/professions",
          element: <ProfessionsList />,
        },
        {
          path: "tags/specialties",
          element: <SpecialtiesList />,
        },
        {
          path: "tags/disease-categories",
          element: <DiseaseCategoriesList />,
        },
        {
          path: "tags/keywords",
          element: <KeywordsList />,
        },
        {
          path: "tags/dci",
          element: <DCIList />,
        },
        {
          path: "tags/therapeutics",
          element: <TherapeuticsList />,
        },
        {
          path: "tags/organization-types",
          element: <OrganizationTypesList />,
        },
        {
          path: "tags/forme-galeniques",
          element: <FormeGaleniquesList />,
        },
        {
          path: "tags/formation-categories",
          element: <FormationCategoriesList />,
        },
        {
          path: "tags/symptoms",
          element: <SymptomsList />,
        },
        {
          path: "tags/causes",
          element: <CausesList />,
        },
        {
          path: "tags/risk-factors",
          element: <RiskFactorsList />,
        },
        {
          path: "tags/generic-groups",
          element: <GenericGroupsList />,
        },
        {
          path: "tags/administration-routes",
          element: <AdministrationRoutesList />,
        },
        {
          path: "tags/event-types",
          element: <EventTypesList />,
        },
        {
          path: "tags/blog-categories",
          element: <BlogCategoriesList />,
        },
        {
          path: "source-levels",
          element: <SourceLevels />,
        },
        {
          path: "add-formation",
          element: <AddFormation />,
        },
        {
          path: "list-formations",
          element: <ListFormations />,
        },
        {
          path: "edit-formation/:id",
          element: <EditFormation />,
        },
        {
          path: "formations/import-zip-review",
          element: <ImportZipReviewFormations />,
        },
        {
          path: "add-module",
          element: <Navigate to="/admin/list-modules" replace />,
        },
        {
          path: "list-modules",
          element: <ListModules />,
        },
        {
          path: "edit-module/:id",
          element: <EditModule />,
        },
        {
          path: "list-certificates",
          element: <ListCertificats />,
        },
        {
          path: "add-certificate",
          element: <AddCertificate />,
        },
        {
          path: "edit-certificate/:id",
          element: <EditCertificate />,
        },
        {
          path: "notifications",
          element: <Notifications />,
        },
        {
          path: "contributions",
          element: <ContributionsOverview />,
        },
        {
          path: "contributions/moderate/:type",
          element: <ContributionModerationHub />,
        },
        {
          path: "contributions/moderate/:type/:id",
          element: <ContributionModerationDetail />,
        },
        {
          path: "contributions/:id",
          element: <ContributionDetail />,
        },
        {
          path: "reports/:id",
          element: <ReportDetail />,
        },
        {
          path: "requests/profession-change/:id",
          element: <ProfessionChangeRequestDetail />,
        },
        // Newsletter Management Routes
        {
          path: "newsletters",
          element: <NewsletterDashboard />,
        },
        {
          path: "newsletters/create",
          element: <NewsletterCreate />,
        },
        {
          path: "newsletters/:id/edit",
          element: <NewsletterCreate />,
        },
        {
          path: "newsletters/:id/analytics",
          element: <NewsletterAnalytics />,
        },
        {
          path: "newsletters/subscribers",
          element: <NewsletterSubscribers />,
        },
        // Library Management Routes
        {
          path: "add-library",
          element: <AddLibrary />,
        },
        {
          path: "list-library",
          element: <ListLibrary />,
        },
        {
          path: "edit-library/:id",
          element: <EditLibrary />,
        },
        {
          path: "tags/library-categories",
          element: <LibraryCategoriesList />,
        },
        // Banner Management Routes
        {
          path: "list-banners",
          element: <ListBanners />,
        },
        {
          path: "add-banner",
          element: <AddBanner />,
        },
        {
          path: "edit-banner/:id",
          element: <EditBanner />,
        },
        // Gamification Routes
        {
          path: "gamification",
          element: <Gamification />,
        },
        {
          path: "gamification/category/veille",
          element: <VeilleCategory />,
        },
        {
          path: "gamification/category/apprentissage",
          element: <ApprentissageCategory />,
        },
        {
          path: "gamification/category/communaute",
          element: <CommunauteCategory />,
        },
        {
          path: "gamification/category/informations",
          element: <InformationsCategory />,
        },
        {
          path: "gamification/badges",
          element: <BadgesManagement />,
        },
        {
          path: "gamification/rewards",
          element: <RewardsManagement />,
        },
        // Waitlist Routes
        {
          path: "waitlist",
          element: <Waitlist />,
        },
        // Hero Management Routes
        {
          path: "heromanagement",
          element: <HeroManagement />,
        },
        // Insights Routes
        {
          path: "insights",
          element: <Insights />,
        },
        // Hero Insights Management Routes
        {
          path: "hero-insights-management",
          element: <HeroInsightsManagement />,
        },
      ],
    },
    {
      path: "*",
      element: <MaintenanceError />,
    },
  ],
};

export default AdminRoutes;
